import React from 'react';
import { useTranslation } from 'react-i18next';

import { Typography, createClasses } from '@kp/react-ui';

const classes = createClasses({
  content: {
    padding: '16px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  list: {
    listStyle: 'disc',
    margin: '8px 0px 0px 24px',
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  numberedList: {
    listStyle: 'decimal',
    margin: '8px 0px 0px 24px',
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  subsection: {
    marginLeft: '16px',
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const LegalDataPrivacy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className={classes.content}>
      <Typography variant="headlineMedium">
        {t('legals.dataPrivacy.title')}
      </Typography>

      {/* Section 1 - Responsible Party */}
      <Typography variant="titleLarge">
        1. {t('legals.dataPrivacy.section1.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section1.paragraph1')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section1.paragraph2')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section1.paragraph3')}
      </Typography>

      {/* Section 2 - Data Protection Officer */}
      <Typography variant="titleLarge">
        2. {t('legals.dataPrivacy.section2.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section2.paragraph1')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section2.paragraph2')}
      </Typography>

      {/* Section 3 - Supervisory Authority */}
      <Typography variant="titleLarge">
        3. {t('legals.dataPrivacy.section3.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section3.paragraph1')}
      </Typography>

      {/* Section 4 - Data Protection Rights */}
      <Typography variant="titleLarge">
        4. {t('legals.dataPrivacy.section4.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section4.paragraph1')}
      </Typography>
      <ul className={classes.list}>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section4.right1')}
        </Typography>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section4.right2')}
        </Typography>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section4.right3')}
        </Typography>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section4.right4')}
        </Typography>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section4.right5')}
        </Typography>
      </ul>

      {/* Section 5 - Right to Object */}
      <Typography variant="titleLarge">
        5. {t('legals.dataPrivacy.section5.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section5.paragraph1')}
      </Typography>

      {/* Section 6 - Hosting */}
      <Typography variant="titleLarge">
        6. {t('legals.dataPrivacy.section6.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section6.paragraph1')}
      </Typography>

      {/* Section 7 - Data Transfer */}
      <Typography variant="titleLarge">
        7. {t('legals.dataPrivacy.section7.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section7.paragraph1')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section7.paragraph2')}
      </Typography>

      {/* Section 8 - Data Processing */}
      <Typography variant="titleLarge">
        8. {t('legals.dataPrivacy.section8.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section8.introduction')}
      </Typography>

      {/* Subsection 8.1 */}
      <div className={classes.subsection}>
        <Typography variant="titleMedium">
          8.1. {t('legals.dataPrivacy.section8.subsection1.title')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection1.description')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection1.details')}
        </Typography>
        <ul className={classes.list}>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection1.dataPoint1')}
          </Typography>
        </ul>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection1.purposeTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection1.purpose')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection1.legalBasis')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection1.noObjection')}
        </Typography>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection1.retentionTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection1.retention')}
        </Typography>
      </div>

      {/* Subsection 8.2 */}
      <div className={classes.subsection}>
        <Typography variant="titleMedium">
          8.2. {t('legals.dataPrivacy.section8.subsection2.title')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection2.description')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection2.details')}
        </Typography>
        <ul className={classes.list}>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection2.dataPoint1')}
          </Typography>
        </ul>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection2.purposeTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection2.purpose')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection2.noObjection')}
        </Typography>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection2.retentionTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection2.retention')}
        </Typography>
      </div>

      {/* Subsection 8.3 */}
      <div className={classes.subsection}>
        <Typography variant="titleMedium">
          8.3. {t('legals.dataPrivacy.section8.subsection3.title')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection3.description')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection3.details')}
        </Typography>
        <ul className={classes.list}>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection3.dataPoint1')}
          </Typography>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection3.dataPoint2')}
          </Typography>
        </ul>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection3.purposeTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection3.purpose')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection3.noObjection')}
        </Typography>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection3.retentionTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection3.retention1')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection3.retention2')}
        </Typography>
      </div>

      {/* Subsection 8.4 */}
      <div className={classes.subsection}>
        <Typography variant="titleMedium">
          8.4. {t('legals.dataPrivacy.section8.subsection4.title')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.description')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.details1')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.details2')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.details3')}
        </Typography>
        <ul className={classes.list}>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection4.dataPoint1')}
          </Typography>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection4.dataPoint2')}
          </Typography>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection4.dataPoint3')}
          </Typography>
          <Typography component="li" variant="bodyMedium">
            {t('legals.dataPrivacy.section8.subsection4.dataPoint4')}
          </Typography>
        </ul>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection4.purposeTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.purpose')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.noObjection')}
        </Typography>
        <Typography variant="bodyMedium">
          <strong>
            {t('legals.dataPrivacy.section8.subsection4.retentionTitle')}
          </strong>
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.retention1')}
        </Typography>
        <Typography variant="bodyMedium">
          {t('legals.dataPrivacy.section8.subsection4.retention2')}
        </Typography>
      </div>

      {/* Section 9 - Service Usage Data Processing */}
      <Typography variant="titleLarge">
        9. {t('legals.dataPrivacy.section9.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section9.description')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section9.details')}
      </Typography>
      <ul className={classes.list}>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section9.dataPoint1')}
        </Typography>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section9.dataPoint2')}
        </Typography>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section9.dataPoint3')}
        </Typography>
      </ul>
      <Typography variant="bodyMedium">
        <strong>{t('legals.dataPrivacy.section9.purposeTitle')}</strong>
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section9.purpose')}
      </Typography>
      <Typography variant="bodyMedium">
        <strong>{t('legals.dataPrivacy.section9.retentionTitle')}</strong>
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section9.retention1')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section9.retention2')}
      </Typography>

      {/* Section 10 - Data Recipients */}
      <Typography variant="titleLarge">
        10. {t('legals.dataPrivacy.section10.title')}
      </Typography>
      <Typography variant="bodyMedium">
        {t('legals.dataPrivacy.section10.paragraph1')}
      </Typography>
      <ul className={classes.list}>
        <Typography component="li" variant="bodyMedium">
          {t('legals.dataPrivacy.section10.subprocessor1')}
        </Typography>
      </ul>
    </div>
  );
};
